import React, { useState } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface HelpTooltipProps {
  /** The content to display inside the tooltip */
  children: React.ReactNode;
  /** Optional custom trigger. Defaults to the small Info icon. */
  trigger?: React.ReactNode;
  /** Optional additional classes for the content */
  contentClassName?: string;
  /** Where to position the tooltip */
  side?: "top" | "right" | "bottom" | "left";
  /** How long to wait before showing the tooltip (ms). Defaults to 0 to match project standard. */
  delayDuration?: number;
}

/**
 * A standardized help tooltip component that uses the small Info icon as a trigger.
 * Ensures consistent font size, spacing, and hover behavior across the app.
 */
const HelpTooltip = ({
  children,
  trigger,
  contentClassName,
  side = "top",
  delayDuration, // Inherit global default if undefined
}: HelpTooltipProps) => {
  // Radix Tooltip only opens on hover/focus, which touch devices never fire, so on mobile
  // tapping the trigger did nothing. Controlling `open` and toggling it on click keeps
  // desktop hover working (Radix still calls onOpenChange for hover) while making tap
  // work too.
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onOpenChange={setOpen} delayDuration={delayDuration}>
      <TooltipTrigger
        asChild
        onClick={(e) => {
          e.preventDefault();
          setOpen((prev) => !prev);
        }}
      >
        {trigger || (
          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help hover:text-primary transition-colors" />
        )}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        className={cn("max-w-xs space-y-1 font-normal", contentClassName)}
      >
        {children}
      </TooltipContent>
    </Tooltip>
  );
};

export default HelpTooltip;
